import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, CircularProgress, Alert, Chip,
  Switch, FormControlLabel, TextField, Checkbox, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CalculateIcon from '@mui/icons-material/Calculate';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { PsfColors } from '../theme';
import {
  fetchNewLaunchUnits, NewLaunchUnit, NewLaunchUnitsResponse, NewLaunchUnitFilters,
  exportPsfPdf,
} from '../services/api';
import { HeaderMenu } from './CompareView';
import type { HeaderRange } from './CompareView';
import type { AmenityPremium } from '../lib/adjustments';
import PsfValueDetailDialog, { PsfValueDetail } from './PsfValueDetailDialog';

/** Full derivation of a personalized PSF, built by BenchmarkView at click time. */
export interface PsfExplain {
  projectName: string;
  basePsf: number;
  age: number | null;
  ageEstimated: boolean;
  remainingLease: number | null;
  level: number;
  methodLabel: string;
  depPct: number | null;
  tenureAdj: number;
  band: { fromAge: number; toAge: number; value: number; mode: 'pct' | 'psf' } | null;
  bandAmount: number;
  extrasApplied: Array<{ label: string; amount: number; value?: number; mode?: 'pct' | 'psf' }>;
  physicalAdj: number;
  amenitiesAdj: number;
  /** Premium setting when the project is beyond the preferred distance, else null. */
  mrtPremium: AmenityPremium | null;
  mrtPremiumAmount: number;
  priSchPremium: AmenityPremium | null;
  priSchPremiumAmount: number;
  /** Current Level 4 settings, so "within preferred distance" can be explained. */
  mrtPremiumSetting?: AmenityPremium;
  priSchPremiumSetting?: AmenityPremium;
  nearestMrtDistance: number | null;
  nearestPriSchDistance: number | null;
  personalizedPsf: number;
}

interface NewLaunchDialogProps {
  open: boolean;
  onClose: () => void;
  psfMax: number | null;
  /** Benchmark tenure — new-launch matches are limited to the same group. */
  tenureGroup: 'leasehold' | 'freehold' | null;
  /** Resale project used as the benchmark reference (display only). */
  contextProjectName?: string | null;
  /** Step-by-step derivation of the personalized PSF. */
  explain?: PsfExplain | null;
  /** Bedroom/size filters from the benchmark table, applied to unit matching. */
  unitFilters?: NewLaunchUnitFilters | null;
  /** Logged-in agent and optional consumer context used on exported reports. */
  reportContext?: {
    userInfo: { Name?: string; Mobile?: string; Email?: string; CEANumber?: string; Photo?: string } | null;
    consumerName?: string | null;
  };
  c: PsfColors;
}

const fmtMoney = (n: number | null) => (n == null ? '—' : `$${n.toLocaleString()}`);
const formatTenure = (value: string | null): string => {
  if (!value) return '—';
  if (/freehold/i.test(value)) return 'Freehold';
  const years = value.match(/\d{2,3}/)?.[0];
  return years || value;
};
const EXECUTIVE_UNIT_TYPE = /executive/i;
const fmtSigned = (n: number) => `${n >= 0 ? '+' : '−'}$${Math.abs(n).toLocaleString()}`;
const fmtAdjustment = (n: number) => (n === 0 ? '$0' : fmtSigned(n));
const fmtMeters = (n: number) => `${Math.round(n).toLocaleString()} m`;
/** One-line explanation of a Level 4 amenity premium for a single project. */
const amenityPremiumText = (
  basePsf: number,
  distance: number | null,
  applied: AmenityPremium | null,
  amount: number,
  setting?: AmenityPremium,
): string => {
  if (applied) {
    const lead = `${fmtMeters(Number(distance))} > preferred ${fmtMeters(applied.preferredDistanceMeters)}`;
    return applied.mode === 'pct'
      ? `${lead}: ${fmtMoney(basePsf)} × ${applied.value}% = ${fmtAdjustment(amount)}`
      : `${lead}: direct premium ${fmtAdjustment(amount)}`;
  }
  if (setting && !setting.enabled) return 'Premium switched off — $0';
  if (distance == null) return 'No distance data — $0';
  return setting
    ? `${fmtMeters(distance)} is within preferred ${fmtMeters(setting.preferredDistanceMeters)} — $0`
    : 'Within preferred distance — $0';
};

type UnitSortDirection = 'default' | 'asc' | 'desc';
type UnitColumnKey =
  | 'projectName' | 'unit' | 'floor' | 'stack' | 'district' | 'topDate' | 'tenure'
  | 'bedroom' | 'areaSqft' | 'price' | 'psf' | 'vsTarget';

type UnitColumn = {
  key: UnitColumnKey;
  label: string;
  filterType: 'multi' | 'range';
  align: 'left' | 'right' | 'center';
  width: number;
};

const UNIT_COLUMNS: UnitColumn[] = [
  { key: 'projectName', label: 'Project', filterType: 'multi', align: 'left', width: 180 },
  { key: 'unit', label: 'Unit', filterType: 'multi', align: 'left', width: 110 },
  { key: 'floor', label: 'Flr', filterType: 'range', align: 'center', width: 62 },
  { key: 'stack', label: 'Stack', filterType: 'range', align: 'center', width: 72 },
  { key: 'district', label: 'District', filterType: 'multi', align: 'center', width: 92 },
  { key: 'topDate', label: 'TOP', filterType: 'range', align: 'center', width: 82 },
  { key: 'tenure', label: 'Tenure', filterType: 'multi', align: 'left', width: 130 },
  { key: 'bedroom', label: 'Bed', filterType: 'range', align: 'center', width: 72 },
  { key: 'areaSqft', label: 'Sqft', filterType: 'range', align: 'right', width: 100 },
  { key: 'price', label: 'Price', filterType: 'range', align: 'right', width: 130 },
  { key: 'psf', label: 'PSF', filterType: 'range', align: 'right', width: 90 },
  { key: 'vsTarget', label: 'vs Target', filterType: 'range', align: 'right', width: 120 },
];

const DEFAULT_UNIT_SORT: { column: UnitColumnKey | null; direction: UnitSortDirection } = {
  column: null,
  direction: 'default',
};

function unitValue(unit: NewLaunchUnit, key: UnitColumnKey, targetPsf: number | null): string | number | null {
  const unitMatch = (unit.unitNumber || '').trim().match(/#?(\d+)-(\d+)/);
  switch (key) {
    case 'projectName': return unit.projectName || '';
    case 'unit': return unit.unitNumber;
    case 'floor': return unit.floor ?? (unitMatch ? Number(unitMatch[1]) : null);
    case 'stack': return unitMatch ? Number(unitMatch[2]) : null;
    case 'district': return unit.district;
    case 'topDate': return unit.topDate;
    case 'tenure': return formatTenure(unit.tenure);
    case 'bedroom': return unit.bedroom;
    case 'areaSqft': return unit.areaSqft;
    case 'price': return unit.price;
    case 'psf': return unit.psf;
    case 'vsTarget': return targetPsf == null ? null : Math.round(unit.psf - targetPsf);
    default: return null;
  }
}

function compareUnitValues(a: string | number | null, b: string | number | null, direction: 1 | -1): number {
  const aMissing = a == null || (typeof a === 'number' && !Number.isFinite(a)) || (typeof a === 'string' && !a.trim());
  const bMissing = b == null || (typeof b === 'number' && !Number.isFinite(b)) || (typeof b === 'string' && !b.trim());
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * direction;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }) * direction;
}

function formatUnitValue(unit: NewLaunchUnit, key: UnitColumnKey, targetPsf: number | null): string {
  const value = unitValue(unit, key, targetPsf);
  switch (key) {
    case 'price': return value == null ? '—' : fmtMoney(value as number);
    case 'psf': return value == null ? '—' : `$${Number(value).toLocaleString()}`;
    case 'vsTarget': {
      if (value == null) return '—';
      const difference = Number(value);
      return difference < 0
        ? `−$${Math.abs(difference).toLocaleString()}`
        : `$${difference.toLocaleString()}`;
    }
    case 'areaSqft': return value == null ? '—' : Math.round(Number(value)).toLocaleString();
    case 'floor':
    case 'stack': return value == null || value === '' ? '—' : String(value);
    case 'bedroom':
    case 'topDate':
    case 'district':
    case 'projectName':
    case 'unit':
    case 'tenure': return value == null || value === '' ? '—' : String(value);
    default: return '—';
  }
}

function BreakdownRow({
  label, detail, amount, c, strong, color, onClick,
}: {
  label: string; detail?: string | null; amount: string;
  c: PsfColors; strong?: boolean; color?: string; onClick?: () => void;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.35 }}>
      <Typography sx={{ fontSize: strong ? '0.78rem' : '0.73rem', fontWeight: strong ? 800 : 600, color: color || c.text }}>
        {label}
      </Typography>
      {detail && (
        <Typography sx={{ fontSize: '0.66rem', color: c.textMuted, flex: 1 }}>
          {detail}
        </Typography>
      )}
      {!detail && <Box sx={{ flex: 1 }} />}
      <Typography onClick={onClick} sx={{
        fontSize: strong ? '0.82rem' : '0.74rem', fontWeight: 800, color: color || c.text,
        whiteSpace: 'nowrap', cursor: onClick ? 'pointer' : 'default',
        textDecoration: 'none',
        '&:hover': onClick ? { color: c.yellow } : {},
      }}>
        {amount}
      </Typography>
    </Box>
  );
}

export default function NewLaunchDialog({
  open, onClose, psfMax, tenureGroup, contextProjectName, explain, unitFilters, reportContext, c,
}: NewLaunchDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<NewLaunchUnitsResponse | null>(null);
  const [showTop5, setShowTop5] = useState(false);
  const [findDivergent, setFindDivergent] = useState(false);
  // null = inherit the benchmark table's Exclude E.C setting; a boolean is the
  // agent's own choice for this dialog. Deriving it (rather than copying it into
  // state on open) keeps the first fetch from firing twice.
  const [excludeExecutiveOverride, setExcludeExecutiveOverride] = useState<boolean | null>(null);
  const [divergenceDraft, setDivergenceDraft] = useState('10');
  const [appliedDivergencePercent, setAppliedDivergencePercent] = useState(10);
  const [unitSort, setUnitSort] = useState<{ column: UnitColumnKey | null; direction: UnitSortDirection }>({ ...DEFAULT_UNIT_SORT });
  const [unitHeaderMulti, setUnitHeaderMulti] = useState<Partial<Record<UnitColumnKey, string[]>>>({});
  const [unitHeaderRanges, setUnitHeaderRanges] = useState<Partial<Record<UnitColumnKey, HeaderRange>>>({});
  const [selectedValueDetail, setSelectedValueDetail] = useState<PsfValueDetail | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const openValueDetail = (label: string, formula: string, amount: string) => {
    setSelectedValueDetail({ label, formula, amount });
  };

  useEffect(() => {
    if (!open) setSelectedValueDetail(null);
  }, [open]);

  useEffect(() => {
    if (open) setExportError(null);
  }, [open, psfMax]);

  const excludeExecutive = excludeExecutiveOverride ?? Boolean(unitFilters?.excludeExecutiveCondominiums);

  const queryPsfMax = findDivergent && psfMax != null
    ? psfMax * (1 + appliedDivergencePercent / 100)
    : psfMax;

  useEffect(() => {
    if (!open || queryPsfMax == null || queryPsfMax <= 0) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    setResult(null);
    setUnitSort({ ...DEFAULT_UNIT_SORT });
    setUnitHeaderMulti({});
    setUnitHeaderRanges({});
    fetchNewLaunchUnits({
      psfMax: queryPsfMax,
      allResults: true,
      tenureGroup: tenureGroup || undefined,
      ...(unitFilters || {}),
      excludeExecutiveCondominiums: excludeExecutive,
    })
      .then((res) => { if (mounted) setResult(res); })
      .catch((e) => { if (mounted) setError(e.message || 'Failed to load new launch units'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
    // unitFilters is snapshotted into dialog state by the parent at click time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, queryPsfMax, tenureGroup, excludeExecutive]);

  useEffect(() => {
    if (!open) return;
    setShowTop5(false);
    setFindDivergent(false);
    setDivergenceDraft('10');
    setAppliedDivergencePercent(10);
    setExcludeExecutiveOverride(null);
  }, [open, psfMax]);

  const applyDivergence = () => {
    const next = Math.min(100, Math.max(0, Number(divergenceDraft) || 0));
    setDivergenceDraft(String(next));
    setAppliedDivergencePercent(next);
  };

  const unitFilterOptions = useMemo(() => {
    const options: Partial<Record<UnitColumnKey, string[]>> = {};
    UNIT_COLUMNS.forEach((column) => {
      if (column.filterType !== 'multi') return;
      const values = (result?.units || [])
        .map((unit) => unitValue(unit, column.key, psfMax))
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
      options[column.key] = Array.from(new Set(values)).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    });
    return options;
  }, [result?.units, psfMax]);

  const filteredSortedUnits = useMemo(() => {
    const source = result?.units || [];
    const indexed = source.map((unit, sourceIndex) => ({ unit, sourceIndex }));
    const filtered = indexed.filter(({ unit }) => {
      if (excludeExecutive && EXECUTIVE_UNIT_TYPE.test(unit.unitTypeDescription || '')) return false;
      for (const [key, values] of Object.entries(unitHeaderMulti) as [UnitColumnKey, string[]][]) {
        if (!values?.length) continue;
        const value = unitValue(unit, key, psfMax);
        if (!values.includes(String(value ?? ''))) return false;
      }
      for (const [key, range] of Object.entries(unitHeaderRanges) as [UnitColumnKey, HeaderRange][]) {
        if (!range?.min && !range?.max) continue;
        const value = unitValue(unit, key, psfMax);
        const numericValue = typeof value === 'number' ? value : Number(value);
        if (!Number.isFinite(numericValue)) return false;
        if (range.min && numericValue < Number(range.min.replace(/,/g, ''))) return false;
        if (range.max && numericValue > Number(range.max.replace(/,/g, ''))) return false;
      }
      return true;
    });

    if (unitSort.column == null || unitSort.direction === 'default') return filtered.map(({ unit }) => unit);
    const direction: 1 | -1 = unitSort.direction === 'asc' ? 1 : -1;
    return filtered
      .slice()
      .sort((a, b) => {
        const valueOrder = compareUnitValues(
          unitValue(a.unit, unitSort.column!, psfMax),
          unitValue(b.unit, unitSort.column!, psfMax),
          direction,
        );
        return valueOrder || (a.sourceIndex - b.sourceIndex) || (a.unit.id - b.unit.id);
      })
      .map(({ unit }) => unit);
  }, [result?.units, unitHeaderMulti, unitHeaderRanges, unitSort, psfMax, excludeExecutive]);

  const displayedUnits = showTop5 ? filteredSortedUnits.slice(0, 5) : filteredSortedUnits;
  const matchingProjectCount = useMemo(
    () => new Set(filteredSortedUnits.map((unit) => unit.projectName)).size,
    [filteredSortedUnits],
  );

  const filterChips: string[] = [];
  if (unitFilters) {
    const { bedroomMin, bedroomMax, areaMin, areaMax } = unitFilters;
    if (bedroomMin != null || bedroomMax != null) {
      filterChips.push(
        bedroomMin != null && bedroomMax != null
          ? (bedroomMin === bedroomMax ? `${bedroomMin} BR` : `${bedroomMin}–${bedroomMax} BR`)
          : bedroomMin != null ? `≥${bedroomMin} BR` : `≤${bedroomMax} BR`,
      );
    }
    if (areaMin != null || areaMax != null) {
      filterChips.push(
        areaMin != null && areaMax != null
          ? `${areaMin.toLocaleString()}–${areaMax.toLocaleString()} sqft`
          : areaMin != null ? `≥${areaMin.toLocaleString()} sqft` : `≤${areaMax!.toLocaleString()} sqft`,
      );
    }
  }

  const handleExportPdf = async () => {
    if (exportingPdf || !explain || !result) return;
    setExportingPdf(true);
    setExportError(null);
    try {
      const calculationSummary: string[] = [
        `Project: ${explain.projectName}`,
        `Base PSF: ${fmtMoney(explain.basePsf)}`,
      ];
      if (explain.level >= 2) {
        calculationSummary.push(explain.depPct != null
          ? `Level 2 tenure: ${fmtMoney(explain.basePsf)} ÷ (1 − ${explain.depPct}%) = ${fmtMoney(explain.basePsf + explain.tenureAdj)}; adjustment ${fmtSigned(explain.tenureAdj)}`
          : 'Level 2 tenure: $0');
      }
      if (explain.level >= 3) {
        if (explain.band) {
          calculationSummary.push(explain.band.mode === 'pct'
            ? `Level 3 age band: ${fmtMoney(explain.basePsf)} × ${explain.band.value}% = ${fmtSigned(explain.bandAmount)}`
            : `Level 3 age band: direct adjustment ${fmtSigned(explain.bandAmount)}`);
        } else calculationSummary.push('Level 3 age band: $0');
        if (explain.extrasApplied.length === 0) calculationSummary.push('Level 3 other adjustments: $0');
        explain.extrasApplied.forEach((extra) => {
          calculationSummary.push(extra.mode === 'pct' && extra.value != null
            ? `Level 3 ${extra.label}: ${fmtMoney(explain.basePsf)} × ${extra.value}% = ${fmtSigned(extra.amount)}`
            : extra.mode === 'psf' && extra.value != null
              ? `Level 3 ${extra.label}: direct adjustment ${fmtSigned(extra.amount)}`
              : `Level 3 ${extra.label}: ${fmtAdjustment(extra.amount)}`);
        });
      }
      if (explain.level >= 4) {
        calculationSummary.push(`MRT premium: ${amenityPremiumText(explain.basePsf, explain.nearestMrtDistance, explain.mrtPremium, explain.mrtPremiumAmount, explain.mrtPremiumSetting)}`);
        calculationSummary.push(`Pri Sch premium: ${amenityPremiumText(explain.basePsf, explain.nearestPriSchDistance, explain.priSchPremium, explain.priSchPremiumAmount, explain.priSchPremiumSetting)}`);
      }
      calculationSummary.push(
        `Final: ${fmtMoney(explain.basePsf)} ${fmtAdjustment(explain.tenureAdj)} ${fmtAdjustment(explain.physicalAdj)} ${fmtAdjustment(explain.mrtPremiumAmount)} ${fmtAdjustment(explain.priSchPremiumAmount)} = ${fmtMoney(explain.personalizedPsf)}`,
      );

      const filterSummary = [...filterChips];
      if (findDivergent) filterSummary.push(`Divergent search: up to ${appliedDivergencePercent}% above ${fmtMoney(psfMax)}`);
      if (excludeExecutive) filterSummary.push('Exclude Executive Apt / E.C');
      if (showTop5) filterSummary.push('Showing top 5 units');
      (Object.entries(unitHeaderMulti) as Array<[UnitColumnKey, string[] | undefined]>).forEach(([key, values]) => {
        if (values?.length) filterSummary.push(`${UNIT_COLUMNS.find((column) => column.key === key)?.label || key}: ${values.join(', ')}`);
      });
      (Object.entries(unitHeaderRanges) as Array<[UnitColumnKey, HeaderRange | undefined]>).forEach(([key, range]) => {
        if (range?.min || range?.max) {
          const rangeText = range.min && range.max ? `${range.min} - ${range.max}` : range.min ? `>= ${range.min}` : `<= ${range.max}`;
          filterSummary.push(`${UNIT_COLUMNS.find((column) => column.key === key)?.label || key}: ${rangeText}`);
        }
      });
      if (unitSort.column && unitSort.direction !== 'default') {
        filterSummary.push(`Sorted by ${UNIT_COLUMNS.find((column) => column.key === unitSort.column)?.label || unitSort.column}: ${unitSort.direction === 'desc' ? 'descending' : 'ascending'}`);
      }

      await exportPsfPdf({
        userInfo: reportContext?.userInfo || null,
        consumerName: reportContext?.consumerName || undefined,
        reportTitle: 'NEW LAUNCH UNIT COMPARISON',
        viewLabel: 'New Launch Units',
        subject: explain.projectName,
        coverSubject: `Benchmark vs new launch · ${fmtMoney(explain.personalizedPsf)} personalized PSF`,
        calculationSummary,
        filterSummary: filterSummary.length ? filterSummary : ['No table filters applied'],
        resultSummary: [
          `${displayedUnits.length.toLocaleString()} units shown`,
          `${matchingProjectCount} project${matchingProjectCount === 1 ? '' : 's'} matched`,
        ],
        columns: UNIT_COLUMNS.map((column) => column.label),
        rows: displayedUnits.map((unit) => UNIT_COLUMNS.map((column) => formatUnitValue(unit, column.key, psfMax))),
        filename: `new-launch-units-${Date.now()}.pdf`,
      });
    } catch (exportFailure: any) {
      setExportError(exportFailure?.message || 'Unable to generate the report');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { width: '80vw', maxWidth: '80vw', height: '80vh', maxHeight: '80vh', bgcolor: c.surfaceCard, border: `1px solid ${c.border}`, borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5, bgcolor: `${c.orange}1e`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <RocketLaunchIcon sx={{ color: c.orange, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: c.text }}>
            New Launch Units under {psfMax != null ? `$${Math.round(psfMax).toLocaleString()} PSF` : '—'}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: c.textMuted }}>
            {contextProjectName ? `Benchmarked against ${contextProjectName} · ` : ''}
            {tenureGroup === 'leasehold' ? 'Leasehold (≤103 yrs)' : tenureGroup === 'freehold' ? '999-yr / Freehold' : 'All tenures'}
            {' · '}available units, cheapest first
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
          onClick={() => void handleExportPdf()}
          disabled={exportingPdf || !displayedUnits.length || !explain}
          sx={{ color: c.textMuted, textTransform: 'none', fontSize: '0.72rem', border: `1px solid ${c.border}`, borderRadius: 1.5, px: 1.2, whiteSpace: 'nowrap' }}
        >
          {exportingPdf ? 'Exporting…' : 'Export PDF'}
        </Button>
        <IconButton size="small" onClick={onClose} sx={{ color: c.textMuted }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {/* ── Derivation ── */}
        {explain && (
          <Box sx={{
            mb: 2, p: 1.5, borderRadius: 1.5,
            bgcolor: `${c.yellow}0d`, border: `1px solid ${c.yellow}44`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <CalculateIcon sx={{ fontSize: 16, color: c.yellow }} />
              <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: c.text }}>
                Calculation breakdown — {explain.projectName}
              </Typography>
            </Box>

            <BreakdownRow
              label="Avg transacted PSF"
              detail={explain.age != null
                ? `age ${explain.age}y${explain.ageEstimated ? ' (est.)' : ''}${explain.remainingLease != null ? ` · ${explain.remainingLease}y lease left` : ''}`
                : 'no age data — raw PSF kept'}
              amount={fmtMoney(explain.basePsf)}
              c={c}
              onClick={() => openValueDetail('Base PSF', `Average transacted PSF = ${fmtMoney(explain.basePsf)}`, fmtMoney(explain.basePsf))}
            />

            {explain.level >= 2 && (
              <BreakdownRow
                label={`Level 2 · ${explain.methodLabel}`}
                detail={explain.depPct != null
                  ? `−${explain.depPct}% vs new → restore: $${explain.basePsf.toLocaleString()} ÷ (1 − ${explain.depPct}%)`
                  : 'No age data — adjustment not applied'}
                amount={fmtSigned(explain.tenureAdj)}
                c={c}
                color={c.orange}
                onClick={() => openValueDetail(
                  'Level 2 · Tenure restoration',
                  explain.depPct != null
                    ? `${fmtMoney(explain.basePsf)} ÷ (1 − ${explain.depPct}%) = ${fmtMoney(explain.basePsf + explain.tenureAdj)}`
                    : 'No age data — Level 2/3 age-based adjustment not applied',
                  fmtSigned(explain.tenureAdj),
                )}
              />
            )}

            {explain.level >= 3 && explain.band && (
              <BreakdownRow
                label="Level 3 · Age band"
                detail={`${explain.band.fromAge}–${explain.band.toAge} yrs @ ${explain.band.value}${explain.band.mode === 'pct' ? '%' : ' $psf'}`}
                amount={fmtSigned(explain.bandAmount)}
                c={c}
                color={c.yellow}
                onClick={() => openValueDetail(
                  'Level 3 · Age band',
                  explain.band!.mode === 'pct'
                    ? `${fmtMoney(explain.basePsf)} × ${explain.band!.value}% = ${fmtSigned(explain.bandAmount)}`
                    : `Direct adjustment = ${fmtSigned(explain.bandAmount)}`,
                  fmtSigned(explain.bandAmount),
                )}
              />
            )}
            {explain.level >= 3 && explain.extrasApplied.map((x) => (
              <BreakdownRow
                key={x.label}
                label="Level 3 · Extra"
                detail={x.label}
                amount={fmtSigned(x.amount)}
                c={c}
                color={c.yellow}
                onClick={() => openValueDetail(
                  `Level 3 · ${x.label}`,
                  x.mode === 'pct' && x.value != null
                    ? `${fmtMoney(explain.basePsf)} × ${x.value}% = ${fmtSigned(x.amount)}`
                    : x.mode === 'psf' && x.value != null
                      ? `Direct adjustment = ${fmtSigned(x.value)}`
                      : `Applied adjustment = ${fmtSigned(x.amount)}`,
                  fmtSigned(x.amount),
                )}
              />
            ))}

            {explain.level >= 4 && ([
              {
                key: 'mrt', label: 'Level 4 · MRT premium', distance: explain.nearestMrtDistance,
                premium: explain.mrtPremium, amount: explain.mrtPremiumAmount, setting: explain.mrtPremiumSetting,
              },
              {
                key: 'pri-sch', label: 'Level 4 · Pri Sch premium', distance: explain.nearestPriSchDistance,
                premium: explain.priSchPremium, amount: explain.priSchPremiumAmount, setting: explain.priSchPremiumSetting,
              },
            ].map((row) => {
              const text = amenityPremiumText(explain.basePsf, row.distance, row.premium, row.amount, row.setting);
              return (
                <BreakdownRow
                  key={row.key}
                  label={row.label}
                  detail={text}
                  amount={fmtAdjustment(row.amount)}
                  c={c}
                  color={c.orange}
                  onClick={() => openValueDetail(row.label, text, fmtAdjustment(row.amount))}
                />
              );
            }))}

            <Box sx={{ borderTop: `1px dashed ${c.yellow}66`, mt: 0.5, pt: 0.5 }}>
              <BreakdownRow
                label="Personalized PSF (new-launch equivalent)"
                amount={fmtMoney(explain.personalizedPsf)}
                c={c}
                strong
                color={c.yellow}
                onClick={() => openValueDetail(
                  'Personalized PSF',
                  `${fmtMoney(explain.basePsf)} + ${fmtSigned(explain.tenureAdj)} + ${fmtSigned(explain.physicalAdj)} + ${fmtSigned(explain.mrtPremiumAmount)} + ${fmtSigned(explain.priSchPremiumAmount)} = ${fmtMoney(explain.personalizedPsf)}`,
                  fmtMoney(explain.personalizedPsf),
                )}
              />
            </Box>
          </Box>
        )}

        <PsfValueDetailDialog
          detail={selectedValueDetail}
          c={c}
          onClose={() => setSelectedValueDetail(null)}
        />

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} sx={{ color: c.orange }} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
        {!loading && !error && result && (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={`${filteredSortedUnits.length.toLocaleString()} matching unit${filteredSortedUnits.length === 1 ? '' : 's'}`}
                sx={{ bgcolor: `${c.orange}1e`, color: c.orange, fontWeight: 700, fontSize: '0.68rem' }}
              />
              <Chip
                size="small"
                label={`${matchingProjectCount} project${matchingProjectCount === 1 ? '' : 's'}`}
                sx={{ bgcolor: `${c.navy}22`, color: c.textMuted, fontWeight: 600, fontSize: '0.68rem' }}
              />
              {filterChips.map((label) => (
                <Chip
                  key={label}
                  size="small"
                  label={label}
                  sx={{ bgcolor: `${c.navyLight}33`, color: c.text, fontWeight: 600, fontSize: '0.68rem' }}
                />
              ))}
            </Box>

            <Box sx={{
              mb: 1.5, p: 1.25, borderRadius: 1.5, bgcolor: `${c.yellow}0d`,
              border: `1px solid ${c.yellow}44`, display: 'flex', alignItems: 'center',
              gap: 1.5, flexWrap: 'wrap',
            }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={<Switch size="small" checked={showTop5} onChange={(e) => setShowTop5(e.target.checked)} />}
                label={<Typography sx={{ color: c.text, fontSize: '0.73rem', fontWeight: 700 }}>Show Top 5</Typography>}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Switch size="small" checked={findDivergent} onChange={(e) => setFindDivergent(e.target.checked)} />}
                  label={<Typography sx={{ color: c.text, fontSize: '0.73rem', fontWeight: 700 }}>
                    Find divergent search around ${Math.round(psfMax || 0).toLocaleString()}
                  </Typography>}
                />
                <TextField
                  size="small"
                  type="number"
                  value={divergenceDraft}
                  disabled={!findDivergent}
                  onChange={(e) => setDivergenceDraft(e.target.value)}
                  onBlur={applyDivergence}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      applyDivergence();
                      e.currentTarget.blur();
                    }
                  }}
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  sx={{ width: 72, '& input': { py: 0.55, fontSize: '0.73rem', color: c.text } }}
                />
                <Typography sx={{ color: c.textMuted, fontSize: '0.75rem' }}>%</Typography>
              </Box>
              <FormControlLabel
                sx={{ m: 0, ml: 'auto' }}
                control={(
                  <Checkbox
                    size="small"
                    checked={excludeExecutive}
                    onChange={(e) => setExcludeExecutiveOverride(e.target.checked)}
                  />
                )}
                label={(
                  <Typography sx={{ color: c.text, fontSize: '0.73rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    Exclude Executive Apt / E.C
                  </Typography>
                  )}
                />
            </Box>
            {exportError && <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}>{exportError}</Alert>}
            <>
              <TableContainer sx={{ maxHeight: '65vh', overflowX: 'auto', border: `1px solid ${c.border}`, borderRadius: 1 }}>
                  <Table stickyHeader size="small" sx={{ minWidth: 1240, tableLayout: 'fixed', borderCollapse: 'separate' }}>
                    <colgroup>
                      {UNIT_COLUMNS.map((column) => <col key={column.key} style={{ width: column.width }} />)}
                    </colgroup>
                    <TableHead>
                      <TableRow>
                        {UNIT_COLUMNS.map((column) => (
                          <TableCell
                            key={column.key}
                            align={column.align}
                            sx={{
                              width: column.width,
                              minWidth: column.width,
                              bgcolor: c.navyDark,
                              color: c.headerText,
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              whiteSpace: 'nowrap',
                              py: 0.75,
                              px: 0.75,
                              ...(column.key === 'projectName' ? {
                                position: 'sticky', left: 0, zIndex: 8,
                                boxShadow: `2px 0 5px ${c.navyDark}88`,
                              } : {}),
                              ...(column.key === 'vsTarget' ? {
                                position: 'sticky', right: 0, zIndex: 10,
                                minWidth: column.width,
                                boxSizing: 'border-box',
                                backgroundColor: c.navyDark,
                                boxShadow: `-2px 0 5px ${c.navyDark}88`,
                              } : {}),
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start', gap: 0.25, whiteSpace: 'nowrap' }}>
                              <span>{column.label}</span>
                              <HeaderMenu
                                filterType={column.filterType}
                                options={unitFilterOptions[column.key] || []}
                                multiValue={unitHeaderMulti[column.key] || []}
                                rangeValue={unitHeaderRanges[column.key] || { min: '', max: '' }}
                                onMultiChange={(value) => setUnitHeaderMulti((previous) => ({ ...previous, [column.key]: value }))}
                                onRangeChange={(value) => setUnitHeaderRanges((previous) => ({ ...previous, [column.key]: value }))}
                                onSort={(direction) => setUnitSort(direction === 'default'
                                  ? { ...DEFAULT_UNIT_SORT }
                                  : { column: column.key, direction })}
                                sortable
                                sortByThisColumn={unitSort.column === column.key && unitSort.direction !== 'default'}
                                sortDir={unitSort.direction === 'desc' ? 'desc' : 'asc'}
                                defaultSortActive={unitSort.direction === 'default'}
                                c={c}
                                groupThousands={['areaSqft', 'price', 'psf', 'vsTarget'].includes(column.key)}
                              />
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedUnits.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={UNIT_COLUMNS.length} align="center" sx={{ py: 4, color: c.textMuted }}>
                            {result.units.length === 0 ? (
                              <>
                                <Typography sx={{ color: c.textMuted, fontSize: '0.85rem' }}>
                                  No available new-launch unit{filterChips.length > 0 ? ' matching these filters' : ''} is currently
                                  {findDivergent ? ` priced at or below ${appliedDivergencePercent}% above this PSF.` : ' priced below this PSF.'}
                                </Typography>
                                <Typography sx={{ color: c.textMuted, fontSize: '0.72rem', mt: 0.5 }}>
                                  The resale option looks competitive at this personalized PSF.
                                </Typography>
                              </>
                            ) : (
                              <Typography sx={{ color: c.textMuted, fontSize: '0.85rem' }}>
                                No units match the active table filters.
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ) : displayedUnits.map((u: NewLaunchUnit, i: number) => (
                        <TableRow key={u.id} hover sx={{ bgcolor: i % 2 === 0 ? c.surfaceCard : c.surface }}>
                          {UNIT_COLUMNS.map((column) => {
                            const value = formatUnitValue(u, column.key, psfMax);
                            const raw = unitValue(u, column.key, psfMax);
                            const isTargetDifference = column.key === 'vsTarget';
                            return (
                              <TableCell
                                key={column.key}
                                align={column.align}
                                title={value}
                                sx={{
                                  width: column.width,
                                  minWidth: column.width,
                                  maxWidth: column.width,
                                  boxSizing: 'border-box',
                                  fontSize: '0.75rem',
                                  color: isTargetDifference
                                    ? (typeof raw === 'number' && raw <= 0 ? c.success : c.danger)
                                    : c.text,
                                  fontWeight: isTargetDifference || column.key === 'psf' || column.key === 'projectName' ? 700 : undefined,
                                  borderColor: c.border,
                                  py: 0.75,
                                  px: 0.75,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  ...(column.key === 'projectName' ? {
                                    position: 'sticky', left: 0, zIndex: 3,
                                    bgcolor: `${i % 2 === 0 ? c.surfaceCard : c.surface} !important`,
                                    boxShadow: `2px 0 5px ${c.navyDark}66`,
                                  } : {}),
                                  ...(column.key === 'vsTarget' ? {
                                    position: 'sticky', right: 0, zIndex: 6,
                                    backgroundColor: i % 2 === 0 ? c.surfaceCard : c.surface,
                                    boxShadow: `-2px 0 5px ${c.navyDark}66`,
                                  } : {}),
                                }}
                              >
                                {value}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography sx={{ fontSize: '0.65rem', color: c.textMuted, mt: 1.5 }}>
                  Prices follow Super Picker availability (live status &amp; pricing where enabled). A unit cheaper than the
                  personalized PSF suggests the new launch may be the better buy at today’s asking price.
                </Typography>
            </>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
